import { makeStyles } from '@paintscout/ui'
import type { MuiTheme as Theme } from '@paintscout/ui'
import React from 'react'
import type { QuoteView } from '../Quote'
import NotesView from '../Quote/views/NotesView'
import ArchivedItemsViewNext from './ArchivedItemsViewNext'
import QuoteNextView from './QuoteNextView'
import QuoteNextWorkOrderView from './QuoteNextWorkOrderView'

export interface QuoteNextProps {
  view?: QuoteView
  /**
   * Whether the document renders inside a presentation. Forwarded straight through to
   * `QuoteNextView`, the same way `Quote` forwards it to the legacy `QuoteView` — see
   * `inPresentation` on `QuoteProps`.
   */
  inPresentation?: boolean
  /** See `showMediaSection` on `QuoteProps` — forwarded straight through to `QuoteNextView`. */
  showMediaSection?: boolean
}

const useStyles = makeStyles(
  (theme: Theme) => ({
    // White card matching the estimate document/section cards, so Notes doesn't float on the grey page.
    notesCard: {
      border: `1px solid ${theme.paintscout.palette.brand.Neutral.P85}`,
      borderRadius: theme.borderRadius.lg,
      backgroundColor: theme.paintscout.palette.brand.Neutral.White,
      padding: theme.spacing(2),
      marginBottom: theme.spacing(2)
    }
  }),
  { name: 'QuoteNext' }
)

/**
 * Document renderer for the new estimate editor (estimateNext). Dispatches the active tab the same
 * way the shared `Quote` does, but renders the fresh `QuoteNextView` (redesigned header + reused
 * tables) for the estimate tab, the redesigned `ArchivedItemsViewNext` for the Archived tab, and
 * `QuoteNextWorkOrderView` (redesigned header + summary, legacy tables) for the Work Order tab.
 * Notes reuses the existing view for now and will be rebuilt as the redesign progresses. Reads the
 * quote from context (the editor supplies the EditableQuoteContextProvider), so it takes no data props.
 */
export default function QuoteNext({ view = 'quote', inPresentation, showMediaSection }: QuoteNextProps) {
  const classes = useStyles()
  switch (view) {
    case 'work-order':
      return <QuoteNextWorkOrderView />
    case 'archived-items':
      return <ArchivedItemsViewNext />
    case 'notes':
      return (
        <div className={classes.notesCard}>
          <NotesView />
        </div>
      )
    default:
      return <QuoteNextView inPresentation={inPresentation} showMediaSection={showMediaSection} />
  }
}
